/**
 * EventCard — shared match card for the landing page + sport pages.
 *
 * Mirrors the match-detail clarity: 🏠 Home / ✈ Away labels, Home/Draw/Away
 * odds buttons (not cryptic 1/X/2), favourite ★, and a "→ R145" return hint.
 */
import { Link } from 'react-router-dom';
import { useBetSlip } from '../contexts/BetSlipContext';
import { rands, totalReturn, favouriteTag } from '../utils/betting';
import { teamFlag } from '../utils/flags';
import { matchPhase, countdownTo, useNow } from '../utils/matchTiming';
import type { EventSummary, EventDetail, Market } from '../services/api';

function findMatchWinner(markets: Market[]): Market | null {
  return (
    markets.find(
      (m) =>
        m.status === 'open' &&
        (m.name.toLowerCase().includes('match winner') ||
          m.name.toLowerCase().includes('who will win') ||
          m.name.toLowerCase().includes('1x2') ||
          m.name.toLowerCase() === 'match result'),
    ) ?? markets.find((m) => m.market_type === 'match_winner') ?? null
  );
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-ZA', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface OddsPickProps {
  label: string;
  odds: number;
  isFav: boolean;
  selected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

function OddsPick({ label, odds, isFav, selected, onClick }: OddsPickProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 rounded-button border px-1 py-1.5 transition ${
        selected
          ? 'border-primary bg-primary text-white shadow-btn'
          : 'border-border bg-surface-2 text-text-primary hover:border-primary hover:bg-primary/5'
      }`}
    >
      <span className={`flex items-center gap-0.5 text-[10px] font-medium leading-none ${selected ? 'text-white/80' : 'text-text-muted'}`}>
        {isFav && <span aria-hidden="true">★</span>}
        {label}
      </span>
      <span className="font-mono text-sm font-bold tabular-nums">{odds.toFixed(2)}</span>
      <span className={`text-[9px] leading-none ${selected ? 'text-white/70' : 'text-text-muted'}`}>
        → {rands(totalReturn(odds))}
      </span>
    </button>
  );
}

export default function EventCard({
  event,
  detail,
}: {
  event: EventSummary;
  detail?: EventDetail | null;
}) {
  const { addLeg, legs } = useBetSlip();
  useNow(1000); // tick the countdown
  const market = detail ? findMatchWinner(detail.markets) : null;
  const phase = event.status === 'finished' ? 'ended' : matchPhase(event.starts_at);
  const isLive = phase === 'inplay';

  const home = market?.selections.find((s) => s.name === event.home_team);
  const draw = market?.selections.find((s) => s.name.toLowerCase() === 'draw');
  const away = market?.selections.find((s) => s.name === event.away_team);
  const allOdds = market ? market.selections.map((s) => s.odds) : [];
  const eventName = `${event.home_team} vs ${event.away_team}`;

  const pick = (selId: string, label: string, odds: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!market) return;
    addLeg({
      selection_id: selId,
      market_id: market.id,
      event_id: event.id,
      event_name: eventName,
      market_name: 'Who will win?',
      selection_name: label,
      odds,
      stake_cents: 0,
    });
  };
  const sel = (id: string) => legs.some((l) => l.selection_id === id);

  return (
    <Link
      to={`/event/${event.id}`}
      className="flex flex-col rounded-card border border-border bg-white p-4 shadow-card transition hover:border-primary/30 hover:shadow-card-hover active:scale-[0.99]"
    >
      {/* League · time / LIVE */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium text-text-muted">{event.league}</span>
        {phase === 'inplay' ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-cta/10 px-2 py-0.5 text-[10px] font-bold text-cta">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cta" /> In play
          </span>
        ) : phase === 'ended' ? (
          <span className="shrink-0 rounded-pill bg-surface-2 px-2 py-0.5 text-[10px] font-bold text-text-muted">Ended</span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
            ⏱ {countdownTo(event.starts_at)}
          </span>
        )}
      </div>

      {/* Teams with home/away labels */}
      <div className="mb-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 truncate">
            <span className="shrink-0 rounded-pill bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary">🏠 Home</span>
            {teamFlag(event.home_team) && <span className="shrink-0 text-base leading-none" aria-hidden="true">{teamFlag(event.home_team)}</span>}
            <span className="truncate text-sm font-bold text-text-primary">{event.home_team}</span>
          </span>
          {isLive && event.home_score !== null && (
            <span className="shrink-0 font-mono text-sm font-bold text-cta tabular-nums">{event.home_score}</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 truncate">
            <span className="shrink-0 rounded-pill bg-surface-2 px-1.5 py-0.5 text-[9px] font-bold uppercase text-text-muted">✈ Away</span>
            {teamFlag(event.away_team) && <span className="shrink-0 text-base leading-none" aria-hidden="true">{teamFlag(event.away_team)}</span>}
            <span className="truncate text-sm font-bold text-text-primary">{event.away_team}</span>
          </span>
          {isLive && event.away_score !== null && (
            <span className="shrink-0 font-mono text-sm font-bold text-cta tabular-nums">{event.away_score}</span>
          )}
        </div>
      </div>

      {/* Absolute kick-off time (upcoming matches) */}
      {phase === 'upcoming' && (
        <p className="mb-2 text-[11px] text-text-muted">🗓 {fmtDate(event.starts_at)}</p>
      )}

      {/* Odds */}
      {detail === null || detail === undefined ? (
        <div className="flex gap-2" aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="shimmer h-[52px] flex-1 rounded-button" />)}
        </div>
      ) : market && home && away ? (
        <div className="flex gap-2" role="group" aria-label="Who will win? odds">
          <OddsPick label="Home" odds={home.odds} isFav={favouriteTag(home.odds, allOdds) === 'favourite'} selected={sel(home.id)} onClick={pick(home.id, event.home_team, home.odds)} />
          {draw && (
            <OddsPick label="Draw" odds={draw.odds} isFav={favouriteTag(draw.odds, allOdds) === 'favourite'} selected={sel(draw.id)} onClick={pick(draw.id, 'Draw', draw.odds)} />
          )}
          <OddsPick label="Away" odds={away.odds} isFav={favouriteTag(away.odds, allOdds) === 'favourite'} selected={sel(away.id)} onClick={pick(away.id, event.away_team, away.odds)} />
        </div>
      ) : (
        <span className="text-xs font-medium text-primary">View all bets →</span>
      )}

      <p className="mt-2 text-[10px] text-text-muted">Tap an odds box to bet · ★ = favourite · → shows R100 return</p>
    </Link>
  );
}
